// @core/stores/project/ProjectComponentStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { projectApi } from '@core/api';
import type {
  AddItemToComponentRequest,
  CreateComponentRequest,
  ItemResponse,
  ProjectComponent,
  UpdateComponentRequest,
} from '@core/types/project';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ProjectComponentStore {
  // List state
  items: ProjectComponent[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: ProjectComponent | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, ProjectComponent>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): ProjectComponent | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: ProjectComponent | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add item to component
   */
  async AddItemToComponent(project_id: string, component_id: string, data: AddItemToComponentRequest): Promise<ItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.AddItemToComponent(project_id, component_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create component
   */
  async CreateComponent(project_id: string, data: CreateComponentRequest): Promise<ProjectComponent> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.CreateComponent(project_id, data);
      const item = response.component;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete component
   */
  async DeleteComponent(project_id: string, component_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await projectApi.DeleteComponent(project_id, component_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== component_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === component_id) {
          this.currentItem = null;
        }

        this.removeFromCache(component_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get component
   */
  async GetComponent(project_id: string, component_id: string): Promise<ProjectComponent> {
    // Check cache first
    const cached = this.cache.get(component_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await projectApi.GetComponent(project_id, component_id);
      const item = response.component;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List components
   */
  async ListComponents(project_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await projectApi.ListComponents(project_id, params);

      runInAction(() => {
        this.items = response.components?.components ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove item from component
   */
  async RemoveItemFromComponent(project_id: string, component_id: string, item_id: string): Promise<ItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.RemoveItemFromComponent(project_id, component_id, item_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update component
   */
  async UpdateComponent(project_id: string, component_id: string, data: UpdateComponentRequest): Promise<ProjectComponent> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.UpdateComponent(project_id, component_id, data);
      const item = response.component;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const projectComponentStore = new ProjectComponentStore();
